import { ref, computed, watch } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import type { MemberAddressParams, BlockItem, KanaStepA } from "../types";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { useAddressDialogNavigation } from "@/components/address-selector/composables/useAddressDialogNavigation";
import { useAddressSelectorCore } from "@/components/address-selector/composables/useAddressSelectorCore";
import {
  createMemberAddressProvider,
  type MemberAddressProviderContext,
} from "@/components/address-selector/providers/memberAddressProvider";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  GetPrefByChainDocument,
  type GetPrefByChainQuery,
  GetPrefCitySectionByZipCodeDocument,
  type GetPrefCitySectionByZipCodeQuery,
} from "@/generated/graphql";

/**
 * 会員編集画面の住所選択ロジックを扱う composable。
 * 旧実装の公開APIを維持しつつ、コア+providerに移行する。
 */
export const useMemberAddress = (params: MemberAddressParams) => {
  const {
    errdialog,
    errMessage,
    addressDialog,
    areaDialog,
    editMember,
    wordList,
    prefListItems,
  } = params;

  const panel = ref<number[]>([0]);

  const blockList = ref<BlockItem[]>([]);
  const unassignedBlockList = ref<Array<string | null | undefined>>([]);
  const zipAddressList = ref(undefined);

  const sectionBlockList = ref<KanaStepA | null | undefined>(null);
  const zip_code = ref("");
  const prefSelectValue = ref("");
  const clickMode = ref(false);

  const btnNonMemberToggle = ref(0);
  const userErrorMessage = ref("");
  const errorZipCode = ref("");
  const errorPrefSelect = ref("");
  const zipCodeFromPref = ref("");
  const cityBlockList = ref<KanaStepA | null | undefined>(null);
  const originalAddress = ref("");
  const originalCityAddress = ref("");
  const originalSectionAddress = ref("");
  const deliveryAreaName = ref("");

  const provider = createMemberAddressProvider();
  const providerContext: MemberAddressProviderContext = {
    createMember: { zip_code: "", address1: "" },
    wordList: wordList.value,
  };

  /** provider 反映先を現在の編集中会員へ同期する。 */
  const syncProviderTarget = (): boolean => {
    providerContext.wordList = wordList.value;
    if (!editMember.value) {
      errdialog.value = true;
      errMessage.value = "会員情報が見つかりません。";
      return false;
    }

    providerContext.createMember = editMember.value;
    return true;
  };

  watch(
    wordList,
    (value) => {
      providerContext.wordList = value;
    },
    { deep: true }
  );

  const {
    state,
    clearErrors,
    clearAreaState,
    setDialog,
    searchByZip,
    loadCityListByPref,
    selectCity,
    selectSection,
    selectBlock,
  } = useAddressSelectorCore<MemberAddressProviderContext>({
    provider,
    context: providerContext,
  });

  /** 画面stateをコアstateへ同期する。 */
  const syncCoreFromRefs = (): void => {
    state.zipCode = zip_code.value;
    state.pref = prefSelectValue.value;
    state.city = originalCityAddress.value;
    state.section = originalSectionAddress.value;
    state.block = "";
    state.cityBlockList = cityBlockList.value as unknown as typeof state.cityBlockList;
    state.sectionBlockList = sectionBlockList.value as unknown as typeof state.sectionBlockList;
    state.blockList = (blockList.value ?? []).filter(
      (value): value is { name: string | null | undefined; enabled: boolean } =>
        value !== null && value !== undefined
    );
    state.errors.zip = errorZipCode.value;
    state.errors.pref = errorPrefSelect.value;
    state.errors.general = userErrorMessage.value;
    state.dialog.addressDialog = addressDialog.value;
    state.dialog.areaDialog = areaDialog.value;
  };

  /** コアstateを画面stateへ同期する。 */
  const syncRefsFromCore = (): void => {
    zip_code.value = state.zipCode;
    prefSelectValue.value = state.pref;
    cityBlockList.value = state.cityBlockList as unknown as KanaStepA | null | undefined;
    sectionBlockList.value = state.sectionBlockList as unknown as KanaStepA | null | undefined;
    blockList.value = state.blockList;
    errorZipCode.value = state.errors.zip;
    errorPrefSelect.value = state.errors.pref;
    userErrorMessage.value = state.errors.general;
    addressDialog.value = state.dialog.addressDialog;
    areaDialog.value = state.dialog.areaDialog;
    unassignedBlockList.value = state.blockList
      .filter((value) => !value.enabled)
      .map((value) => value.name || wordList.value.lbl_other_block_disp_name);
  };

  /** エリア選択状態を初期化する。 */
  const areaClear = async (): Promise<void> => {
    clearAreaState();
    state.city = "";
    state.section = "";
    originalCityAddress.value = "";
    originalSectionAddress.value = "";
    deliveryAreaName.value = "";
    syncRefsFromCore();
  };

  // 住所ダイアログのクローズ/戻る分岐は共通フックへ委譲する。
  const { onCloseAddressDialog, onClickAreaDialogReturn } = useAddressDialogNavigation({
    state,
    btnNonMemberToggle,
    originalCityAddress,
    originalSectionAddress,
    unassignedBlockList,
    syncCoreFromRefs,
    syncRefsFromCore,
    setDialog,
    areaClear,
    onBackToAddressDialog: () => {
      deliveryAreaName.value = "";
    },
  });

  /** 郵便番号選択 @Click */
  const onClickToggleZip = async (): Promise<void> => {
    clearErrors();
    btnNonMemberToggle.value = 0;
    syncCoreFromRefs();
    syncRefsFromCore();
  };

  /** 都道府県選択 @Click */
  const onClickTogglePref = async (): Promise<void> => {
    clearErrors();
    btnNonMemberToggle.value = 1;
    syncCoreFromRefs();
    syncRefsFromCore();
  };

  /** 都道府県入力イベント @input */
  const onPrefSelectInput = async (): Promise<void> => {
    syncCoreFromRefs();
    state.pref = prefSelectValue.value;
    clearErrors();
    syncRefsFromCore();
  };

  /** 郵便番号入力イベント @input */
  const onZipCodeInput = async (value?: string): Promise<void> => {
    if (typeof value === "string") {
      zip_code.value = value;
    }

    errorZipCode.value = "";
    userErrorMessage.value = "";
    errorPrefSelect.value = "";

    if (zip_code.value.length !== 7) {
      return;
    }

    const apolloClient = await generateApolloClient();
    const result: ApolloQueryResult<GetPrefCitySectionByZipCodeQuery> =
      await apolloClient.query<GetPrefCitySectionByZipCodeQuery>({
        query: GetPrefCitySectionByZipCodeDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
          zip_code: zip_code.value,
        },
        fetchPolicy: "no-cache",
      });

    if (!result.data.getPrefCitySectionByZipCode || result.data.getPrefCitySectionByZipCode.length === 0) {
      errorZipCode.value = "入力した郵便番号はご利用対象外です";
    }
  };

  /**
   * 「会員・会員登録」ボタン @Click
   * 旧公開名を維持しつつ、検索処理はコアへ委譲する。
   */
  const onClickDeliveryRequestAndNewMember = async (): Promise<void> => {
    if (!syncProviderTarget()) {
      return;
    }

    if (clickMode.value) {
      return;
    }

    clickMode.value = true;
    try {
      syncCoreFromRefs();
      clearErrors();
      await areaClear();

      if (btnNonMemberToggle.value === 0) {
        await onZipCodeInput(zip_code.value);
        if (errorZipCode.value.length > 0) {
          return;
        }

        const resolved = await searchByZip();
        syncRefsFromCore();

        if (!resolved) {
          originalCityAddress.value = state.city;
          originalSectionAddress.value = state.section;
        }

        if (resolved) {
          zipCodeFromPref.value = resolved.zip_code;
          state.pref = resolved.pref_name ?? state.pref;
          state.city = resolved.city_name ?? state.city;
          state.section = resolved.section_name ?? state.section;
          prefSelectValue.value = state.pref;
          originalCityAddress.value = state.city;
          originalSectionAddress.value = state.section;
          syncRefsFromCore();
          return;
        }

        if (
          state.blockList.length === 0 &&
          state.cityBlockList === null &&
          state.sectionBlockList === null
        ) {
          // 配達可能な住所がない場合は住所選択ダイアログにエラーを表示する
          errorZipCode.value = "入力した郵便番号はご利用対象外です";
          return;
        }

        originalAddress.value = prefSelectValue.value;
        return;
      }

      if (btnNonMemberToggle.value === 1) {
        state.pref = prefSelectValue.value;
        await loadCityListByPref();
        originalAddress.value = prefSelectValue.value;
        syncRefsFromCore();
      }
    } finally {
      clickMode.value = false;
    }
  };

  /** 市区リンクボタン押下時を処理する。 */
  const onClickCityBlockLink = async (item: string | null | undefined): Promise<void> => {
    if (!item || clickMode.value || !syncProviderTarget()) {
      return;
    }

    clickMode.value = true;
    try {
      syncCoreFromRefs();
      await selectCity(item);
      originalCityAddress.value = item;
      originalSectionAddress.value = "";
      syncRefsFromCore();
    } catch (err: unknown) {
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "町村の取得に失敗しました。";
      await areaClear();
    } finally {
      clickMode.value = false;
    }
  };

  /** 町域リンクボタン押下時を処理する。 */
  const onClickSectionBlockLink = async (item: string | null | undefined): Promise<void> => {
    if (!item || clickMode.value || !syncProviderTarget()) {
      return;
    }

    clickMode.value = true;
    try {
      syncCoreFromRefs();
      const resolved = await selectSection(item);
      originalSectionAddress.value = item === "その他" ? "" : item;
      syncRefsFromCore();

      if (resolved) {
        zipCodeFromPref.value = resolved.zip_code;
        areaDialog.value = false;
      }
    } catch (err: unknown) {
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
      await areaClear();
    } finally {
      clickMode.value = false;
    }
  };

  /** 丁目リンクボタン押下時を処理する。 */
  const onClickAddressBlockLink = async (item: string | null | undefined): Promise<void> => {
    if (!item || clickMode.value || !syncProviderTarget()) {
      return;
    }

    clickMode.value = true;
    try {
      syncCoreFromRefs();
      const resolved = await selectBlock(item);
      syncRefsFromCore();

      if (resolved) {
        zipCodeFromPref.value = resolved.zip_code;
        areaDialog.value = false;
      }
    } catch (err: unknown) {
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
    } finally {
      clickMode.value = false;
    }
  };

  /** 住所選択画面に遷移する。 */
  const onClickAddressUpdate = async (): Promise<void> => {
    const apolloClient = await generateApolloClient();

    const prefList: ApolloQueryResult<GetPrefByChainQuery> =
      await apolloClient.query<GetPrefByChainQuery>({
        query: GetPrefByChainDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
        },
        fetchPolicy: "no-cache",
      });

    prefListItems.value = prefList.data.getPrefByChain ?? [];

    if (editMember.value?.zip_code) {
      zip_code.value = editMember.value.zip_code;
    }

    state.zipCode = zip_code.value;
    state.pref = prefSelectValue.value;
    setDialog(true, false);
    syncRefsFromCore();
  };

  /** 配達対象外区画存在メッセージ取得＆置換 */
  const areaNotSelectLabel = computed((): string => {
    const hasUnassignedInCurrentBlocks = (blockList.value ?? []).some(
      (block) => block && !block.enabled
    );
    if (!hasUnassignedInCurrentBlocks || unassignedBlockList.value.length === 0) {
      return "";
    }
    const unassignedBlocks = unassignedBlockList.value.join(", ");
    return wordList.value.lbl_area_not_select?.replace("{{s}}", unassignedBlocks) || "";
  });

  return {
    panel,
    blockList,
    unassignedBlockList,
    zipAddressList,
    sectionBlockList,
    zip_code,
    prefSelectValue,
    clickMode,
    btnNonMemberToggle,
    userErrorMessage,
    errorZipCode,
    errorPrefSelect,
    zipCodeFromPref,
    cityBlockList,
    originalAddress,
    originalCityAddress,
    originalSectionAddress,
    deliveryAreaName,
    onClickToggleZip,
    onClickTogglePref,
    onPrefSelectInput,
    onClickDeliveryRequestAndNewMember,
    onClickCityBlockLink,
    onClickSectionBlockLink,
    onClickAddressBlockLink,
    areaClear,
    onClickAddressUpdate,
    onCloseAddressDialog,
    onClickAreaDialogReturn,
    onZipCodeInput,
    areaNotSelectLabel,
  };
};
